import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Paperclip, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalTechniciansPanel } from "@/pages/ExternalTechniciansPanel";
import { ExternalHoursPanel } from "@/pages/ExternalHoursPanel";

const fmt = (d) => new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
const hm = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
const TABS = { da_confermare: "Da confermare", confermato: "Confermati", scartato: "Scartati" };

const ClientPicker = ({ s, value, onPick }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return undefined; }
    const t = setTimeout(() => api.get("/crm/companies", { params: { q, limit: 8 } }).then((r) => setRes(r.data.items || [])).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const opt = (id, name, extra, tid) => (
    <button type="button" key={id} onClick={() => onPick({ id, name })} data-testid={tid}
      className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${value?.id === id ? "border-brand-600 bg-brand-50" : "border-line hover:bg-slate-50"}`}>
      <span className="truncate">{name}</span><span className="ml-2 shrink-0 text-xs text-slate-400">{extra}</span></button>
  );
  return (
    <div className="space-y-2">
      <Label className="text-xs">Cliente · il tecnico ha scritto «{s.client_text}»</Label>
      {s.suggestions.length ? <div className="space-y-1.5" data-testid="tech-suggestions">
        <p className="flex items-center gap-1 text-xs text-slate-500"><Sparkles className="h-3.5 w-3.5" />Clienti suggeriti</p>
        {s.suggestions.map((x) => opt(x.company_id, x.name, `${Math.round(x.score * 100)}%${x.city ? " · " + x.city : ""}`, `tech-suggestion-${x.company_id}`))}
      </div> : <p className="text-xs text-amber-700">Nessun cliente simile trovato: cercalo qui sotto.</p>}
      <Input placeholder="Cerca un altro cliente…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tech-client-search" />
      {res.map((c) => opt(c.id, c.name, c.city || "", `tech-search-${c.id}`))}
    </div>
  );
};

const ConfirmDialog = ({ s, onClose, onDone }) => {
  const [client, setClient] = useState(s.suggestions[0]?.score >= 0.85 ? { id: s.suggestions[0].company_id, name: s.suggestions[0].name } : null);
  const [itvs, setItvs] = useState([]);
  const [itv, setItv] = useState("new");
  const [techs, setTechs] = useState([]);
  const [tech, setTech] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/interventions/meta").then((r) => setTechs(r.data.technicians || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!client) { return; }
    api.get(`/tech-submissions/${s.id}/interventions`, { params: { company_id: client.id } }).then((r) => { setItvs(r.data.items); setItv(r.data.items[0]?.id || "new"); }).catch(() => {});
  }, [client, s.id]);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tech-submissions/${s.id}/confirm`, { company_id: client.id, intervention_id: itv === "new" ? null : itv, lead_technician_id: tech || null });
      toast.success(`Attività svolta registrata sull'intervento ${r.data.intervention_number}`); onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tech-confirm-dialog">
        <DialogHeader><DialogTitle>Conferma resoconto di {s.technician}</DialogTitle></DialogHeader>
        <ClientPicker s={s} value={client} onPick={setClient} />
        {client && <div className="space-y-1.5"><Label className="text-xs">Intervento</Label>
          <Select value={itv} onValueChange={setItv}>
            <SelectTrigger data-testid="tech-intervention-select"><SelectValue /></SelectTrigger>
            <SelectContent>{itvs.map((i) => <SelectItem key={i.id} value={i.id}>{i.number} · {i.title}</SelectItem>)}<SelectItem value="new">Nuovo intervento</SelectItem></SelectContent>
          </Select></div>}
        {client && itv === "new" && techs.length > 0 && <div className="space-y-1.5"><Label className="text-xs">Tecnico di riferimento dell'intervento</Label>
          <Select value={tech} onValueChange={setTech}>
            <SelectTrigger data-testid="tech-lead-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>{techs.map((t) => <SelectItem key={t.key || t.id} value={t.key || t.id}>{t.label || t.name}</SelectItem>)}</SelectContent>
          </Select></div>}
        <p className="text-xs text-slate-500">Diventa un'attività completata di {hm(s.minutes)}: dall'intervento la riporti nel rapportino e segue il solito iter (fatturabile o scalata dal pacchetto ore).</p>
        <DialogFooter><Button onClick={go} disabled={busy || !client} data-testid="tech-confirm-submit">{busy ? "Conferma…" : "Conferma e crea attività"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Item = ({ s, onConfirm, onChanged }) => {
  const [why, setWhy] = useState(null);
  const open = async () => {
    try { const r = await api.get(`/tech-submissions/${s.id}/attachment`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const discard = async () => {
    try { await api.post(`/tech-submissions/${s.id}/discard`, { reason: why }); toast.success("Resoconto scartato"); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="space-y-2 p-4" data-testid={`tech-sub-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold text-ink">{s.client_text}</p><span className="text-xs text-slate-500">· {s.technician} · {fmt(s.start_at)} → {fmt(s.end_at)} · {hm(s.minutes)}</span>
        {s.status !== "da_confermare" && <StatusBadge tone={s.status === "confermato" ? "green" : "neutral"}>{TABS[s.status]}</StatusBadge>}
      </div>
      <p className="whitespace-pre-line text-sm text-slate-700">{s.activity}</p>
      {s.materials && <p className="text-xs text-slate-600"><b>Materiale:</b> {s.materials}</p>}
      {s.notes && <p className="text-xs text-slate-600"><b>Note:</b> {s.notes}</p>}
      {s.discard_reason && <p className="text-xs text-slate-500">Motivo: {s.discard_reason}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {s.attachment && <Button size="sm" variant="outline" className="gap-1" onClick={open} data-testid={`tech-attachment-${s.id}`}><Paperclip className="h-3.5 w-3.5" />{s.attachment.filename}</Button>}
        {s.status === "da_confermare" && <>
          <Button size="sm" onClick={() => onConfirm(s)} data-testid={`tech-confirm-${s.id}`}>Conferma</Button>
          {why === null ? <Button size="sm" variant="ghost" onClick={() => setWhy("")} data-testid={`tech-discard-${s.id}`}>Scarta</Button>
            : <div className="flex w-full gap-2 sm:w-auto"><Textarea rows={1} placeholder="Motivo" value={why} onChange={(e) => setWhy(e.target.value)} data-testid={`tech-discard-reason-${s.id}`} />
              <Button size="sm" variant="outline" disabled={why.trim().length < 3} onClick={discard} data-testid={`tech-discard-submit-${s.id}`}>Scarta</Button></div>}
        </>}
        {s.intervention_id && <a href={`/interventi/${s.intervention_id}`} className="text-xs font-semibold text-brand-700 hover:underline" data-testid={`tech-itv-link-${s.id}`}>Apri intervento</a>}
      </div>
    </div>
  );
};

export default function TechSubmissionsPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState("da_confermare");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/tech-submissions", { params: { status: tab } }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e))), [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = params.get("id");
    if (id && data) { const s = data.items.find((x) => x.id === id); if (s && s.status === "da_confermare") setSel(s); }
  }, [params, data]);
  return (
    <div data-testid="tech-submissions-page">
      <PageHeader overline="Interventi" title="Moduli tecnici" description="Resoconti inviati dai tecnici esterni dal modulo sul sito. Scegli il cliente e l'intervento: diventano attività svolte." />
      <ExternalTechniciansPanel />
      <ExternalHoursPanel />
      <div className="mb-3 flex gap-2">{Object.entries(TABS).map(([k, l]) => (
        <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} onClick={() => setTab(k)} data-testid={`tech-tab-${k}`}>{l}{k === "da_confermare" && data ? ` (${data.pending})` : ""}</Button>))}</div>
      <Panel className="divide-y divide-line">
        {(data?.items || []).map((s) => <Item key={s.id} s={s} onConfirm={setSel} onChanged={load} />)}
        {data && !data.items.length && <p className="p-6 text-sm text-slate-500" data-testid="tech-empty">Nessun resoconto.</p>}
      </Panel>
      {sel && <ConfirmDialog s={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} />}
    </div>
  );
}
